import React, { useRef, useEffect, useState, useMemo } from "react";
import { DataTable, ActionButton, Tag } from "./ui";
import "./products.css";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "./Modals/ConfirmModal";
import { MDBContainer, MDBTooltip } from "mdb-react-ui-kit";
import { api } from "../services";
import { getImageUrl } from "../utils/utils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Toolbar from "./Toolbar";
import Actionbar from "./Actionbar";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DeleteModal from "./Modals/DeleteModal";
import ProductPriceWithApps from "./ProductPriceWithApps";
import {
  isPosReadOnlyEntity,
  isPosIntegrationDeleteLocked,
  posReadOnlyTooltip,
  posDeleteLockedTooltip,
} from "../utils/posReadOnly";
import { fetchPricedAppSources, getUniqueConnectedDeliveryApps } from "../utils/productAppPrice";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import {
  resolveDateLocale,
  formatLocalizedDateTime,
} from "../utils/formatDateTime";

const getProductSortApiField = (column, isArabic) => {
  const map = {
    name: isArabic ? "name.ar" : "name.en",
    category: "category_id",
    store: "store",
    price: "price",
    createdAt: "createdAt",
  };
  return map[column];
};

function Products() {
  const {t, i18n} = useTranslation("translation")
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can, canAny } = usePermission();
  const canAddProduct = can(RBAC_MODULES.PRODUCTS, PERMISSION_ACTIONS.ADD);
  const canSeeProductDetail = canAny([
    [RBAC_MODULES.PRODUCTS, PERMISSION_ACTIONS.VIEW],
    [RBAC_MODULES.PRODUCTS, PERMISSION_ACTIONS.EDIT],
  ]);
  const canDeleteProduct = can(RBAC_MODULES.PRODUCTS, PERMISSION_ACTIONS.DELETE);
  const [viewMode, setViewMode] = useState("list");
  const [itemsData, setItemsData] = useState([]); // Fetched or Saved Items
  const [savedItems, setSavedItems] = useState([]); // Backup Items (Saved in DB)
  const [apps, setApps] = useState([]);
  const [addonApps, setAddonApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("Select App");
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [modalData, setModalData] = useState({
    title: "",
    message: "",
    action: () => {},
    actionData: null,
    visibility: null,
  });

  const [showDeleteModal,setShowDeleteModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [loading, setLoading] = useState(true); // <-- Add loading state
  const [showProductDeleteModal, setShowProductDeleteModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeMap, setStoreMap] = useState({});
  const [selectedStoreFilter, setSelectedStoreFilter] = useState("");
  const [selectedStoreOption, setSelectedStoreOption] = useState(null);
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [productSortField, setProductSortField] = useState("createdAt");
  const [productSortDirection, setProductSortDirection] = useState("desc");
  const navigate = useNavigate();

  const skipInitialPageFetch = useRef(true);
  const skipInitialStoreFilterFetch = useRef(true);
  const skipInitialSearchFetch = useRef(true);
  const skipInitialPageSizeFetch = useRef(true);
  const skipInitialSortFetch = useRef(true);

  const DUMMY_IMAGE = process.env.PUBLIC_URL + "/default.jpg";
  const deliveryApps = useMemo(
    () => getUniqueConnectedDeliveryApps(apps, addonApps),
    [apps, addonApps]
  );

  useEffect(() => {
    setLoading(true); // Start loading before fetch
    fetchApps();
    fetchStores();
    fetchItems();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedAppId) {
      setLoading(true);
      fetchItems(selectedAppId);
    }
    // eslint-disable-next-line
  }, [selectedAppId]);

  useEffect(() => {
    if (skipInitialPageFetch.current) {
      skipInitialPageFetch.current = false;
      return;
    }
    setLoading(true);
    fetchItems();
    // eslint-disable-next-line
  }, [currentPage]);

  useEffect(() => {
    if (skipInitialPageSizeFetch.current) {
      skipInitialPageSizeFetch.current = false;
      return;
    }
    setCurrentPage(1);
    setLoading(true);
    fetchItems();
    // eslint-disable-next-line
  }, [itemsPerPage]);

  useEffect(() => {
    if (skipInitialStoreFilterFetch.current) {
      skipInitialStoreFilterFetch.current = false;
      return;
    }
    setCurrentPage(1);
    setLoading(true);
    fetchItems();
    // eslint-disable-next-line
  }, [selectedStoreFilter]);

  useEffect(() => {
    if (skipInitialSearchFetch.current) {
      skipInitialSearchFetch.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setCurrentPage(1);
      setLoading(true);
      fetchItems();
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchQuery]);

  useEffect(() => {
    if (skipInitialSortFetch.current) {
      skipInitialSortFetch.current = false;
      return;
    }
    setCurrentPage(1);
    setLoading(true);
    fetchItems(null, productSortField, productSortDirection, 1);
    // eslint-disable-next-line
  }, [productSortField, productSortDirection]);

  // Fetch Apps
  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError(`${t("Unauthorized: Please log in.")}`);
        setLoading(false);
        return;
      }

      const { apps: nextApps, addonApps: nextAddonApps, appsError } =
        await fetchPricedAppSources();
      if (appsError) {
        throw appsError;
      }
      setApps(nextApps);
      setAddonApps(nextAddonApps);
    } catch (error) {
      console.error(
        "Error fetching apps:",
        error.response?.data || error.message
      );
      setError(`${t("Failed to fetch apps.")}`);
    }
  };

  // Fetch Stores (for mapping storeId -> storeName)
  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        return;
      }
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetchedStores = res.data?.stores || [];
      setStores(fetchedStores);
      const map = {};
      fetchedStores.forEach((s) => {
        if (s && s._id) {
          map[s._id] = s.name || s.storeName || "";
        }
      });
      setStoreMap(map);
    } catch (err) {
      // Non-blocking; store mapping is optional for rendering
      console.error("Error fetching stores:", err?.response?.data || err?.message);
    }
  };

  // Fetch Catalog Items (Try API First, If Fails Use Saved Data)
  const fetchItems = async (
    appId = null,
    sortBy = productSortField,
    sortOrder = productSortDirection,
    page = currentPage
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError(`${t("Unauthorized: Please log in.")}`);
        //navigate("/login");
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(itemsPerPage));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      if (selectedStoreFilter) {
        params.set("storeId", selectedStoreFilter);
      }
      const trimmedSearch = (searchQuery || "").trim();
      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }

      const endpoint = `/products?${params.toString()}`;

      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setItemsData(response.data.items || []);
      setSavedItems(response.data.items || []); // ✅ Save Fetched Data in Backup

      setTotalPages(response.data.totalPages ?? 1);
      setTotalItems(
        response.data.totalItems ??
          response.data.paginationEnhanced?.totalItems ??
          (response.data.items || []).length
      );
    } catch (error) {
      console.error(
        "Error fetching catalog items:",
        error.response?.data || error.message
      );
      setError(`${t("Failed to fetch catalog items. Showing saved items.")}`);

      // ✅ If Fetch Fails, Use Last Saved Items
      if (savedItems.length > 0) {
        setItemsData(savedItems);
      }

      // ✅ If Token is Expired, Redirect to Login
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } finally {
      setLoading(false); // Stop loading after fetch
    }
  };

  // Handle App Selection
  const handleAppSelect = (app) => {
    setSelectedApp(app.application);
    setSelectedAppId(app._id);
    fetchItems(app._id);
  };

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle store filter selection from dropdown
  const handleStoreFilterSelect = (store) => {
    const id = store?._id || "";
    setSelectedStoreFilter(id);
    setSelectedStoreOption(store || null);
  };

  // Handle View Mode Change
  const handleViewModeChange = (mode) => setViewMode(mode);

  const applyTableSort = (column, direction) => {
    const apiField = getProductSortApiField(column, isArabic);
    if (!apiField) return;

    setTableSortColumn(column);
    setProductSortField(apiField);
    setProductSortDirection(direction);
  };

  // Handle Add Item Click
  const handleAddItemClick = () => navigate("/add-product");

  // Handle Edit Item Click
  const handleEditItemClick = (item) => {
    console.log("Edit Item Click", item);
    navigate(`/product/${item._id}`);
  };

  //Handle visibility of the Product
  const handleVisibility = async (item, visibility) => {
    console.log("Confirmed!", item);
    const payload = {
      productId: item._id,
      isVisible: visibility,
    };

    try {
      const token = localStorage.getItem("token");
      console.log("🔄 Sending Update visibility Payload:", payload);

      //  Correct API URL
      const response = await api.post(`hide-product`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(" Item visibility updated Successfully:", response.data);

      await fetchItems();
    } catch (error) {
      console.error(
        " Error adding category:",
        error.response?.data || error.message
      );
      setError(error.response?.data?.message || `${t("Failed to add category.")}`);
    }

    setShowConfirm(false); // Hide modal after confirming
  };

  // Helper to resolve store id for a product item
  const getStoreId = (item) =>
    item?.store ||
    item?.storeId ||
    item?.store_id ||
    item?.category_id?.store ||
    item?.category_id?.storeId ||
    item?.category_id?.store_id;

  // Server-side search and filtering; use backend results directly
  const filteredItems = itemsData;

  // Resolve store name for a product item
  const getStoreName = (item) => {
    const id =
      item?.store ||
      item?.storeId ||
      item?.store_id ||
      item?.category_id?.store ||
      item?.category_id?.storeId ||
      item?.category_id?.store_id;
    const nameFromItem = item?.storeName || item?.category_id?.storeName;
    return nameFromItem || (id && storeMap[id]) || "N/A";
  };

  const handleDeleteProduct = async (productId) => {
   
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${t("Product deleted successfully.")}`);
      setDeleteError("");
      fetchItems();
    } catch (error) {
      toast.error(
        error.response?.data?.message || `${t("Failed to delete product")}`
      );
      setDeleteSuccess("");
    } finally {
      setShowProductDeleteModal(false);
      setSelectedProductId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "image",
        header: t("productimage"),
        width: 72,
        minWidth: 72,
        align: "center",
        render: (item) => {
          const src = getProductImageSrc(item);
          return (
            <div className="oh-product-thumb">
              {src ? (
                <img
                  src={src}
                  alt=""
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.removeAttribute("src");
                    e.target.style.display = "none";
                  }}
                />
              ) : null}
            </div>
          );
        },
      },
      {
        id: "name",
        header: t("productname"),
        strong: true,
        sortable: true,
        render: (item) => (
          <NameWithTooltip name={isArabic ? item.name?.ar : item.name?.en} />
        ),
      },
      {
        id: "category",
        header: t("category"),
        sortable: true,
        render: (item) => (
          <MDBTooltip
            tag="span"
            title={isArabic ? item.category_id?.name?.ar : item.category_id?.name?.en || "No Category"}
            placement="top"
          >
            <span className="oh-product-cell-ellipsis" style={{ maxWidth: 120 }}>
              {isArabic ? item.category_id?.name?.ar : item.category_id?.name?.en || "No Category"}
            </span>
          </MDBTooltip>
        ),
      },
      {
        id: "store",
        header: t("storename"),
        sortable: true,
        render: (item) => (
          <MDBTooltip tag="span" title={getStoreName(item)} placement="top">
            <span className="oh-product-cell-ellipsis">
              {getStoreName(item)}
            </span>
          </MDBTooltip>
        ),
      },
      {
        id: "price",
        header: t("price"),
        sortable: true,
        render: (item) => (
          <ProductPriceWithApps product={item} deliveryApps={deliveryApps} />
        ),
      },
      {
        id: "catalogCount",
        header: t("catalogcount"),
        render: (item) =>
          item.catalogCount > 0 ? (
            <Tag variant="linked">
              {t("linked")} ({item.catalogCount})
            </Tag>
          ) : (
            <Tag variant="neutral">{t("notlinked")}</Tag>
          ),
      },
      {
        id: "createdAt",
        header: t("Created At"),
        sortable: true,
        render: (item) => (
          <span className="oh-product-cell-date">
            {formatLocalizedDateTime(item.createdAt, dateLocale)}
          </span>
        ),
      },
      {
        id: "actions",
        header: t("actions"),
        width: 180,
        minWidth: 180,
        nowrap: true,
        className: "oh-table-td--actions",
        headerClassName: "oh-table-th--actions",
        render: (item) => {
          const posView = isPosReadOnlyEntity(item);
          const noPermView = !canSeeProductDetail;
          const viewDisabled = posView || noPermView;
          const viewTooltip = posView
            ? posReadOnlyTooltip(t, item)
            : noPermView
              ? t("noPermissionToAccessThis")
              : undefined;

          const posDel = isPosIntegrationDeleteLocked(item);
          const noPermDel = !canDeleteProduct;
          const catalogBlockDel = item.catalogCount > 0;
          const deleteDisabled = posDel || noPermDel || catalogBlockDel;
          const deleteTooltip = posDel
            ? posDeleteLockedTooltip(t, item)
            : noPermDel
              ? t("noPermissionToAccessThis")
              : catalogBlockDel
                ? t("Delete is disabled because it is linked to a catalog.")
                : undefined;

          return (
            <div
              className="oh-table-actions"
              onClick={(e) => e.stopPropagation()}
            >
              {viewDisabled && viewTooltip ? (
                <MDBTooltip tag="span" title={viewTooltip}>
                  <span className="d-inline-flex">
                    <ActionButton variant="view" disabled>
                      {t("view")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <ActionButton
                  variant="view"
                  onClick={() => handleEditItemClick(item)}
                >
                  {t("view")}
                </ActionButton>
              )}
              {deleteDisabled && deleteTooltip ? (
                <MDBTooltip tag="span" title={deleteTooltip}>
                  <span className="d-inline-flex">
                    <ActionButton variant="delete" disabled>
                      {t("delete")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <ActionButton
                  variant="delete"
                  onClick={() => {
                    setSelectedProductId(item._id);
                    setShowProductDeleteModal(true);
                  }}
                >
                  {t("delete")}
                </ActionButton>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      isArabic,
      dateLocale,
      storeMap,
      canDeleteProduct,
      canSeeProductDetail,
      getStoreName,
      deliveryApps,
    ]
  );

  return (
    <div className="products-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <div className="oh-page-header">
          <h1 className="page-title-header mb-0">{t("products")}</h1>
        </div>

        {/* Display Error Message */}
        {error && <p className="text-danger">{error}</p>}

        {/* App Selection */}
        {/* <MDBRow className="mb-4">
            <MDBCol md="6">
              <label>Select App</label>
              <MDBDropdown>
                <MDBDropdownToggle color="light">
                  {selectedApp}
                </MDBDropdownToggle>
                <MDBDropdownMenu>
                  {apps.map((app) => (
                    <MDBDropdownItem
                      key={app._id}
                      onClick={() => handleAppSelect(app)}
                    >
                      {app.application}
                    </MDBDropdownItem>
                  ))}
                </MDBDropdownMenu>
              </MDBDropdown>
            </MDBCol>
          </MDBRow> */}

        {/* Quick Actions */}
        <div className="oh-page-section">
        <Actionbar
          title={t("quickAction")}
          subtitle={t("manageYouWorkSpace")}
        />
        </div>
        {/* Search and View Toggle */}
        <div className="oh-page-section">
          <Toolbar
            hideIcons={true}
            showSearch={true}
            searchQuery={searchQuery}
            searchPlaceHolder={t("Search Products")}
            handleSearch={handleSearchChange}
            showDropDown2={true}
            options2={stores}
            handleSelect2={handleStoreFilterSelect}
            selectedOption2={selectedStoreOption}
            placeholder2={t("Select store")}
            label2={t("Store")}
            showViewMode={true}
            viewMode={viewMode}
            handleViewChange={setViewMode}
          />
        </div>

        <div className="oh-page-section">
        {/* Items Content */}
        {deleteSuccess && (
          <div className="alert alert-success">{deleteSuccess}</div>
        )}
        {deleteError && <div className="alert alert-danger">{deleteError}</div>}
        {viewMode === "grid" ? (
          loading ? (
            <div className="oh-product-grid">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div className="oh-product-card oh-product-card--skeleton" key={idx}>
                  <Skeleton height={180} />
                  <div style={{ padding: 16 }}>
                    <Skeleton height={20} width="70%" style={{ marginBottom: 8 }} />
                    <Skeleton height={14} width="90%" count={4} style={{ marginBottom: 6 }} />
                    <Skeleton height={32} width="100%" style={{ marginTop: 12 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="oh-product-empty">{t("noProductsToDisplay")}</div>
          ) : (
          <div className="oh-product-grid">
            {filteredItems.map((item) => (
              <ProductGridCard
                key={item._id || item.item_id}
                item={item}
                t={t}
                isArabic={isArabic}
                dateLocale={dateLocale}
                getStoreName={getStoreName}
                canSeeProductDetail={canSeeProductDetail}
                canDeleteProduct={canDeleteProduct}
                deliveryApps={deliveryApps}
                onView={handleEditItemClick}
                onDelete={(id) => {
                  setSelectedProductId(id);
                  setShowProductDeleteModal(true);
                }}
              />
            ))}
          </div>
          )
        ) : (
          <DataTable
            tableId="products-list"
            title={t("products")}
            columns={columns}
            data={filteredItems}
            rowKey="_id"
            loading={loading}
            emptyState={t("noProductsToDisplay")}
            primaryAction={
              canAddProduct
                ? { label: t("addnewproduct"), onClick: handleAddItemClick }
                : {
                    label: t("addnewproduct"),
                    onClick: handleAddItemClick,
                    disabled: true,
                    title: t("noPermissionToAccessThis"),
                  }
            }
            sort={{
              column: tableSortColumn,
              direction: productSortDirection,
              onChange: ({ column, direction }) =>
                applyTableSort(column, direction),
            }}
            pagination={{
              page: currentPage,
              pageSize: itemsPerPage,
              total: totalItems,
              onPageChange: (newPage) => {
                if (newPage >= 1 && newPage <= totalPages) {
                  setCurrentPage(newPage);
                }
              },
              onPageSizeChange: (size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              },
              pageSizeOptions: [10, 20, 50],
              showLabel: t("Show", { defaultValue: "Show" }),
              ofLabel: t("of", { defaultValue: "Of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl: isArabic,
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
            isRtl={isArabic}
          />
        )}
        </div>
        <ConfirmModal
          show={showConfirm}
          onHide={() => setShowConfirm(false)}
          title={modalData.title}
          message={modalData.message}
          action={modalData.action}
          actionData={modalData.actionData}
          visibility={modalData.visibility}
        />
        <DeleteModal
          text={t("areyousureyouwanttodeletethisproduct")}
          showModal={showProductDeleteModal}
          confirmDelete={() => handleDeleteProduct(selectedProductId)}
          onClose={() => setShowProductDeleteModal(false)}
        />
      </MDBContainer>
    </div>
  );
}

function getProductImageSrc(item) {
  if (!item?.image_path) return "";
  return item.image_path.startsWith("http")
    ? item.image_path
    : getImageUrl(item.image_path);
}

function ProductGridCard({
  item,
  t,
  isArabic,
  dateLocale,
  getStoreName,
  canSeeProductDetail,
  canDeleteProduct,
  deliveryApps = [],
  onView,
  onDelete,
}) {
  const productName = isArabic
    ? item.name?.ar || item.name?.en
    : item.name?.en || item.name?.ar || t("No Name");
  const categoryName = isArabic
    ? item.category_id?.name?.ar || item.category_id?.name?.en
    : item.category_id?.name?.en || item.category_id?.name?.ar || t("No Category");

  const posView = isPosReadOnlyEntity(item);
  const noPermView = !canSeeProductDetail;
  const viewDisabled = posView || noPermView;
  const viewTooltip = posView
    ? posReadOnlyTooltip(t, item)
    : noPermView
      ? t("noPermissionToAccessThis")
      : undefined;

  const posDel = isPosIntegrationDeleteLocked(item);
  const noPermDel = !canDeleteProduct;
  const catalogBlockDel = item.catalogCount > 0;
  const deleteDisabled = posDel || noPermDel || catalogBlockDel;
  const deleteTooltip = posDel
    ? posDeleteLockedTooltip(t, item)
    : noPermDel
      ? t("noPermissionToAccessThis")
      : catalogBlockDel
        ? t("Delete is disabled because it is linked to a catalog.")
        : undefined;

  return (
    <article className="oh-product-card">
      <div className="oh-product-card__media">
        {getProductImageSrc(item) ? (
          <img
            src={getProductImageSrc(item)}
            alt={productName}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
            }}
          />
        ) : null}
      </div>
      <div className="oh-product-card__body">
        <h3 className="oh-product-card__title">{productName}</h3>
        <div className="oh-product-card__meta">
          <div className="oh-product-card__meta-row">
            <span className="oh-product-card__meta-label">{t("category")}:</span>
            <span className="oh-product-card__meta-value">{categoryName}</span>
          </div>
          <div className="oh-product-card__meta-row">
            <span className="oh-product-card__meta-label">{t("storename")}:</span>
            <span className="oh-product-card__meta-value">{getStoreName(item)}</span>
          </div>
          <div className="oh-product-card__meta-row">
            <span className="oh-product-card__meta-label">{t("price")}:</span>
            <span className="oh-product-card__meta-value">
              <ProductPriceWithApps product={item} deliveryApps={deliveryApps} />
            </span>
          </div>
          <div className="oh-product-card__meta-row">
            <span className="oh-product-card__meta-label">{t("Catalog Count")}:</span>
            {item.catalogCount > 0 ? (
              <Tag variant="linked">
                {t("linked")} ({item.catalogCount})
              </Tag>
            ) : (
              <Tag variant="neutral">{t("notlinked")}</Tag>
            )}
          </div>
          <div className="oh-product-card__meta-row">
            <span className="oh-product-card__meta-label">{t("Created At")}:</span>
            <span className="oh-product-card__meta-value">
              {formatLocalizedDateTime(item.createdAt, dateLocale)}
            </span>
          </div>
        </div>
        <div className="oh-product-card__footer oh-table-actions">
          {viewDisabled && viewTooltip ? (
            <MDBTooltip tag="span" title={viewTooltip}>
              <span className="d-inline-flex">
                <ActionButton variant="view" disabled>
                  {t("view")}
                </ActionButton>
              </span>
            </MDBTooltip>
          ) : (
            <ActionButton variant="view" onClick={() => onView(item)}>
              {t("view")}
            </ActionButton>
          )}
          {deleteDisabled && deleteTooltip ? (
            <MDBTooltip tag="span" title={deleteTooltip}>
              <span>
                <ActionButton variant="delete" disabled>
                  {t("delete")}
                </ActionButton>
              </span>
            </MDBTooltip>
          ) : (
            <ActionButton
              variant="delete"
              onClick={() => onDelete(item._id)}
            >
              {t("delete")}
            </ActionButton>
          )}
        </div>
      </div>
    </article>
  );
}

function NameWithTooltip({ name }) {
  const spanRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setTruncated(spanRef.current.scrollWidth > spanRef.current.clientWidth);
      }
    };
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [name]);
  return (
    <>
      <span
        ref={spanRef}
        className="oh-product-name-cell"
      >
        {name || "No Name"}
      </span>
      {truncated && (
        <MDBTooltip tag="span" title={name || "No Name"} placement="top">
          <span
            style={{
              marginLeft: 4,
              verticalAlign: "middle",
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <img
              src="/images/information-circle.svg"
              alt=""
              width={14}
              height={14}
              aria-hidden="true"
            />
          </span>
        </MDBTooltip>
      )}
    </>
  );
}

export default Products;
